import { useNavigate } from "react-router-dom";
import { useNews } from "@/hooks/useSupabaseData";

export default function HeroSection() {
  const navigate = useNavigate();
  const { news: headlineNews, loading: hlLoading } = useNews({ isHeadline: true, limit: 1 });
  const { news: featuredNews, loading: ftLoading } = useNews({ isFeatured: true, limit: 3 });

  const headline = headlineNews[0];

  if (hlLoading || ftLoading || !headline) {
    return (
      <section className="w-full pt-20 md:pt-24 pb-8 md:pb-12">
        <div className="w-full px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 animate-pulse">
            <div className="lg:col-span-5 space-y-4">
              <div className="h-6 w-24 bg-alvinegro-light rounded-full"></div>
              <div className="h-12 bg-alvinegro-light rounded-lg"></div>
              <div className="h-4 bg-alvinegro-light rounded"></div>
              <div className="h-4 bg-alvinegro-light rounded w-3/4"></div>
            </div>
            <div className="lg:col-span-4">
              <div className="aspect-[4/5] bg-alvinegro-light rounded-[24px]"></div>
            </div>
            <div className="lg:col-span-3 space-y-4">
              <div className="h-8 bg-alvinegro-light rounded-lg"></div>
              <div className="aspect-[16/10] bg-alvinegro-light rounded-2xl"></div>
              <div className="aspect-[16/10] bg-alvinegro-light rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full pt-20 md:pt-24 pb-8 md:pb-12">
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {/* Main Headline - Left */}
          <div className="lg:col-span-5 flex flex-col justify-center gap-4 md:gap-6">
            <span className="inline-block bg-alvinegro-black text-white text-xs font-semibold px-3 py-1 rounded-full w-fit">
              {headline.category}
            </span>
            <h1 className="font-serif font-bold text-3xl md:text-4xl lg:text-5xl text-alvinegro-black leading-tight">
              {headline.title}
            </h1>
            <p className="text-alvinegro-muted text-base md:text-lg leading-relaxed">
              {headline.subtitle}
            </p>
            <div className="flex items-center gap-3 text-sm text-alvinegro-muted">
              <span>{headline.author}</span>
              <span>•</span>
              <span>{headline.published_at ? new Date(headline.published_at).toLocaleDateString("pt-BR") : ""}</span>
            </div>
            <button
              onClick={() => navigate(`/noticias/${headline.slug}`)}
              className="bg-alvinegro-black text-white px-6 py-3 rounded-full text-sm font-medium w-fit hover:bg-alvinegro-dark transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
            >
              Ler matéria completa
              <i className="ri-arrow-right-up-line"></i>
            </button>
          </div>

          {/* Main Image - Center */}
          <div className="lg:col-span-4">
            <div className="relative rounded-[24px] overflow-hidden aspect-[4/5] md:aspect-[3/4] cursor-pointer group"
              onClick={() => navigate(`/noticias/${headline.slug}`)}>
              <img
                src={headline.image_url || "https://readdy.ai/api/search-image?query=Football%20stadium%20night%20professional%20photography%20dramatic%20lights%20dark%20atmosphere%20black%20and%20white&width=800&height=1000&seq=hero-fallback&orientation=portrait"}
                alt={headline.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
            </div>
          </div>

          {/* Featured News - Right */}
          <div className="lg:col-span-3 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="font-serif font-bold text-lg text-alvinegro-black">Em Destaque</h2>
              <i className="ri-arrow-right-up-line text-alvinegro-muted"></i>
            </div>

            <div className="flex flex-col gap-4">
              {featuredNews.map((news) => (
                <div
                  key={news.id}
                  className="relative rounded-2xl overflow-hidden aspect-[16/10] cursor-pointer group"
                  onClick={() => navigate(`/noticias/${news.slug}`)}
                >
                  <img
                    src={news.image_url || "https://readdy.ai/api/search-image?query=Football%20match%20action%20professional%20sports%20photography%20dramatic&width=800&height=500&seq=featured&orientation=landscape"}
                    alt={news.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <span className="absolute top-3 left-3 bg-alvinegro-black/80 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                    {news.category}
                  </span>
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="text-white font-semibold text-sm leading-snug line-clamp-2">
                      {news.title}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}